import type {
  Task,
  TaskFileSummary,
  TaskStatus,
  TaskPriority,
  TaskSource,
  TaskWithUsers,
  TaskDetailResponse as SharedTaskDetailResponse,
  CreateTaskRequest,
  UpdateTaskRequest,
  ChangeTaskStatusRequest,
  TaskCommentWithFiles,
  CreateTaskCommentRequest,
} from '@doneverse/shared'
import { api } from '../api/index.js'

export interface TaskListResponse {
  tasks: TaskWithUsers[]
  total: number
}

export type TaskDetailResponse = SharedTaskDetailResponse

export interface TaskListFilters {
  status?: TaskStatus
  priority?: TaskPriority
  assigned_to?: string
  source?: TaskSource
  /** Case-insensitive substring match on the task title. */
  search?: string
  /** Restrict to tasks where the current user is the primary or an additional assignee. */
  mine?: boolean
  /** Row ordering: 'deadline' (default), 'priority' (kanban), 'status' (grouped list). */
  order?: 'deadline' | 'priority' | 'status'
  /** Omit internal_only tasks (non-founders previewing the founder view). */
  excludeInternal?: boolean
  /**
   * Omit `completed` tasks — the board's "Hide completed" default.
   *
   * Never send it alongside `status`: the two contradict each other and the
   * server answers with an empty page rather than an error.
   */
  excludeCompleted?: boolean
  /**
   * Restrict to one `kind = 'collection'` label — a **Project** in UI copy.
   * 404s if the id isn't a live collection in this workspace.
   */
  collection_id?: string
  /**
   * Restrict to one `kind = 'list'` label, or to `UNCATEGORISED_LIST_KEY` for
   * the tasks carrying no list at all. A task holds at most one list, so this
   * filter partitions the workspace — which is what lets a list-grouped board
   * page each section on its own.
   */
  list_id?: string
  page?: number
  limit?: number
}

export const taskService = {
  listTasks: async (filters: TaskListFilters = {}): Promise<TaskListResponse> => {
    const params: Record<string, string> = {}
    if (filters.status) params.status = filters.status
    if (filters.priority) params.priority = filters.priority
    if (filters.assigned_to) params.assigned_to = filters.assigned_to
    if (filters.source) params.source = filters.source
    if (filters.search) params.search = filters.search
    if (filters.mine) params.mine = 'true'
    if (filters.order) params.order = filters.order
    if (filters.excludeInternal) params.exclude_internal = 'true'
    if (filters.excludeCompleted) params.exclude_completed = 'true'
    if (filters.collection_id) params.collection_id = filters.collection_id
    if (filters.list_id) params.list_id = filters.list_id
    if (filters.page) params.page = String(filters.page)
    if (filters.limit) params.limit = String(filters.limit)

    return api.query(params).get('/v1/tasks').json<TaskListResponse>()
  },

  getTask: async (id: string): Promise<TaskDetailResponse> => {
    return api.get(`/v1/tasks/${id}`).json<TaskDetailResponse>()
  },

  createTask: async (body: CreateTaskRequest): Promise<Task> => {
    return api.post(body, '/v1/tasks').json<Task>()
  },

  updateTask: async (id: string, body: UpdateTaskRequest): Promise<Task> => {
    return api.patch(body, `/v1/tasks/${id}`).json<Task>()
  },

  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/v1/tasks/${id}`).res()
  },

  changeStatus: async (id: string, body: ChangeTaskStatusRequest): Promise<Task> => {
    return api.patch(body, `/v1/tasks/${id}/status`).json<Task>()
  },

  getComments: async (id: string): Promise<TaskCommentWithFiles[]> => {
    return api.get(`/v1/tasks/${id}/comments`).json<TaskCommentWithFiles[]>()
  },

  addComment: async (taskId: string, body: CreateTaskCommentRequest): Promise<TaskCommentWithFiles> => {
    return api.post(body, `/v1/tasks/${taskId}/comments`).json<TaskCommentWithFiles>()
  },

  attachFiles: async (taskId: string, fileIds: string[]): Promise<{ files: TaskFileSummary[] }> => {
    return api.post({ file_ids: fileIds }, `/v1/tasks/${taskId}/files`).json<{ files: TaskFileSummary[] }>()
  },

  detachFile: async (taskId: string, fileId: string): Promise<void> => {
    await api.delete(`/v1/tasks/${taskId}/files/${fileId}`).res()
  },

  addAssignee: async (taskId: string, userId: string): Promise<void> => {
    await api.post({ user_id: userId }, `/v1/tasks/${taskId}/assignees`).res()
  },

  removeAssignee: async (taskId: string, userId: string): Promise<void> => {
    await api.delete(`/v1/tasks/${taskId}/assignees/${userId}`).res()
  },
}
